/**
 * useCart — load a CartDto by id, expose loading/error + a `reload` fn.
 * Uses AbortSignal cleanup on unmount.
 *
 * Rule: errors shape to a plain string message; loading true until first resolve.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CartDto } from '@retail-ops/shared-types';
import { useApi } from './useApi';

export interface UseCartState {
  cart: CartDto | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useCart(cartId: string | undefined): UseCartState {
  const { client, makeSignal } = useApi();
  const [cart, setCart] = useState<CartDto | null>(null);
  const [loading, setLoading] = useState<boolean>(!!cartId);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    if (!cartId) {
      setCart(null);
      setLoading(false);
      return;
    }

    // Bill 2026-05-11 — stale-while-revalidate. ONLY flip loading=true on
    // the initial fetch (no cart yet). reload() (tick++) does a background
    // refresh keeping `cart` populated so consumers (CartDetail) don't swap
    // to <WaitStateOverlay/> and unmount the whole tree — that was clearing
    // search input + results in GlobalCustomerSearch every time a person
    // was added.
    setError(null);
    const isInitialLoad = cart == null || cart.cartId !== cartId;
    if (isInitialLoad) setLoading(true);
    const signal = makeSignal();

    client.retail.carts.get(cartId, { signal }).then(
      (res): void => {
        if (!res.success || !res.cart) {
          setError(res.error ?? 'Unable to load cart');
          setCart(null);
        } else {
          setCart(res.cart);
        }
        if (isInitialLoad) setLoading(false);
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (isInitialLoad) setLoading(false);
      },
    );
    // makeSignal is stable per-component and client is memoized.
    // cart is read for the "first fetch?" check but we don't want to
    // re-run the effect when cart updates inside this hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, tick]);

  const reload = useCallback((): void => setTick((t) => t + 1), []);

  return { cart, loading, error, reload };
}
